import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  notification,
} from 'antd';
import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import joi from 'joi';
import { cloneDeep, isEmpty } from 'lodash';

import { withRouter } from '../../hooks/withRouter';
import Context from '../../context';
import { createErrorMessage } from '../../context/globalStateHelper';
import NominationForm from '../NominationForm';
import ModalWrapper, { modalConfirmClose } from '../ModalWrapper';
import getNominationSchema from '../../schemas/nominations';
import {
  LOCATION_POINTS,
  DIRECTIONS,
} from '../../constants/nominations';
import {
  isDateAllowed, getEarliestValidTime, getNominationWarnings, getFirstOpenHourIndex,
} from '../../utils/nominationHelpers';
import { userHasPermission } from '../../utils/userHelpers';
import { isDateInRange } from '../../utils/dateTimeHelpers';
import { STATUS } from '../../constants/status';
import { PERMISSIONS } from '../../constants/users';
import {
  startOfGasDay, hoursInGasDay, getGasDay, startOfOngoingGasDay,
} from '../../utils/gasday';
import { getMarketPartyEIC } from '../../utils/marketparty';

const logger = new Logger('new-nomination:form');

class NewNomination extends Component {
  constructor(props) {
    super(props);

    const length = hoursInGasDay(getGasDay(startOfOngoingGasDay()));
    const { nominationData = {}, canViewOnly } = props;

    const nomination = this.isRenomination() || canViewOnly
      ? nominationData
      : {
        amounts: Array.from({ length }, () => null),
        date: getGasDay(startOfOngoingGasDay()),
      };

    this.state = {
      errors: [],
      isSaving: false,
      nomination,
    };
  }

  componentDidMount() {
    const {
      balanceGroupMembers,
      updateBalanceGroupMembers,
      updateRelationships,
      relationships,
    } = this.context;
    if (!balanceGroupMembers) {
      updateBalanceGroupMembers();
    }
    if (!relationships) {
      updateRelationships();
    }
  }

  getClearedAmounts = (length) => {
    const { nomination: { amounts } } = this.state;
    const clearedLength = length || amounts.length;

    return Array.from({ length: clearedLength }, () => null);
  }

  isRenomination = () => {
    const { nominationData = {}, canViewOnly } = this.props;
    return !isEmpty(nominationData) && !canViewOnly;
  }

  isBalanceGroupMemberOnDate = (date) => {
    const { balanceGroupMembers } = this.context;
    return balanceGroupMembers && !!balanceGroupMembers.find(({ status, start, end }) => (
      status === STATUS.ACCEPTED && isDateInRange(getGasDay(date), start, end)
    ));
  }

  isButtonDisabled = () => {
    const {
      errors,
      nomination: {
        amounts,
        date,
        onBehalfOfMarketPartyId,
      },
    } = this.state;
    return errors.length > 0
      || !date
      || !isDateAllowed(date)
      || (!onBehalfOfMarketPartyId && !this.isBalanceGroupMemberOnDate(startOfGasDay(date)))
      || amounts.includes(null)
      || amounts.length !== hoursInGasDay(date);
  }

  closeModal = () => {
    const { navigate } = this.props;

    return navigate('/nominations');
  };

  handleCancel = () => {
    const { t } = this.props;

    return this.isButtonDisabled()
      ? this.closeModal()
      : modalConfirmClose(t, this.closeModal);
  }


  getNominationData = () => {
    const { nomination } = this.state;
    const { amounts, date, point } = nomination || { point: '' };

    const updatedObject = cloneDeep(nomination);

    // remove pointDirection as they are separated now.
    delete updatedObject.pointDirection;
    // stripe out eic code form imatra
    if (point === LOCATION_POINTS.IMATRA) {
      updatedObject.shipperEIC = undefined;
    }
    // only used as reference
    delete updatedObject.counterPartyId;

    const earliestValidTime = getEarliestValidTime();
    amounts.forEach((amount, index) => {
      const time = startOfGasDay(date).add(index, 'hours');
      if (point !== LOCATION_POINTS.VTP && time.isBefore(earliestValidTime)) {
        updatedObject.amounts[index] = 0;
      }
    });

    return updatedObject;
  }

  handleCreate = async () => {
    const {
      selectedMarketPartyId,
      handleDbChange,
      isReadOnlyAdmin,
      addRecentCounterparty,
    } = this.context;
    const { nomination } = this.state;

    if (selectedMarketPartyId === undefined || isReadOnlyAdmin()) return;

    const data = this.getNominationData();
    delete data.nomresAmounts;
    delete data.matchedAmounts;

    const { t } = this.props;
    const nominationSchema = getNominationSchema(data.point);
    const { error } = joi.object(nominationSchema).validate(data);


    if (error) {
      notification.error({
        className: 'notification-error',
        message: t('common.notifications.incorrectInput'),
        description: error.toString(),
      });
      return;
    }

    const { onBehalfOfMarketPartyId } = data;
    const createUrl = `/marketparties/${onBehalfOfMarketPartyId || selectedMarketPartyId}/nominations/nomint`;
    try {
      this.setState({ isSaving: true });
      delete data.onBehalfOfMarketPartyId; // this is not supposed to be sent in the body

      const { body } = await API.post({
        apiName: 'FINTSO',
        path: createUrl,
        options: {
          body: {
            ...data,
            createdByMarketPartyId: selectedMarketPartyId,
          },
        },
      }).response;
      const createdNomint = await body.json();

      handleDbChange('NomInt', 'create', createdNomint);

      if (addRecentCounterparty) {
        addRecentCounterparty(nomination.counterPartyId);
      }

      notification.success({
        message: t('nominations.message.createdSuccessfully'),
        description: '',
        placement: 'bottomRight',
      });

      this.closeModal();
    } catch (e) {
      notification.error({
        className: 'notification-error',
        message: t('nominations.message.errorCreating'),
        description: await createErrorMessage(e),
      });
      logger.error(await createErrorMessage(e, true));
      this.setState({ isSaving: false });
    }
  }

  updateChangedFields = (field, updatedObj, error) => {
    const { errors, nomination } = this.state;
    const updatedErrors = errors.filter(err => err !== field);

    if (error) {
      updatedErrors.push(field);
    }

    const resetZerosToNull = nomination.amounts.map((v) => (v || null));
    const shouldReset = field === 'pointDirection'
      && updatedObj.point === LOCATION_POINTS.VTP
      && updatedObj.amounts.some(amount => amount !== null);

    this.setState({
      errors: updatedErrors,
      nomination: {
        ...updatedObj,
        ...(shouldReset ? { amounts: resetZerosToNull } : undefined),
      },
    });

    return !!error;
  };

  handleAmountsChange = (field, value) => {
    const { nomination } = this.state;
    const index = parseInt(field.split('_')[1], 10);
    const { amounts, point, date } = nomination;
    const { error } = joi.number().validate(value);

    amounts[index] = value;

    // BC flat enforcement: typing in the first open hour auto-fills all later hours
    // with the same value so the nomination stays flat. The last hour (amounts.length - 1)
    // is also filled here but remains editable independently for remainder adjustments.
    // amounts.length already reflects the correct number of hours for the gas day
    // (23 on spring-forward, 24 normally, 25 on fall-back), so no DST special-casing needed.
    if (point === LOCATION_POINTS.BALTIC_CONNECTOR) {
      const firstOpenHour = getFirstOpenHourIndex(date);
      if (index === firstOpenHour) {
        for (let i = firstOpenHour + 1; i < amounts.length; i += 1) {
          amounts[i] = value;
        }
      }
    }

    const updatedObj = {
      ...nomination,
      amounts,
    };

    this.updateChangedFields(field, updatedObj, error);
  }

  handleChange = (field, value, meta) => {
    const { nomination } = this.state;
    const { selectedMarketPartyId } = this.context;
    const { pointDirection } = nomination;
    const [point] = pointDirection ? pointDirection.split('-') : [''];
    const nominationSchema = getNominationSchema(point);

    // special handling for amounts field
    if (field.startsWith('amounts')) {
      this.handleAmountsChange(field, value);

      return;
    }

    const { error } = nominationSchema[field].validate(value);
    const updatedObj = {
      ...nomination,
      [field]: value,
    };

    if (field === 'shipperEIC') {
      updatedObj.counterPartyId = meta.marketPartyId;
    }

    if (field === 'pointDirection') {
      const [pointValue, directionValue] = value ? value.split('-') : [];
      updatedObj.point = pointValue;
      updatedObj.direction = directionValue;
      updatedObj.onBehalfOfMarketPartyId = undefined; // clear onBehalfOfMarketPartyId when point-direction changes
      updatedObj.amounts = this.getClearedAmounts();

      if (pointValue === LOCATION_POINTS.IMATRA) {
        delete updatedObj.shipperEIC; // Clear ´Counterparty` value from state
        updatedObj.direction = DIRECTIONS.ENTRY; // Select `Entry` by default for `Imatra` point
      }
    }

    if (field === 'onBehalfOfMarketPartyId') {
    // when onBehalfOfMarketPartyId is selected, by default set selectedMarketPartyId's EIC
      const shipperEIC = value && point !== LOCATION_POINTS.IMATRA // shipperEIC is forbidden in BE for Imatra
        ? getMarketPartyEIC(this.context, selectedMarketPartyId)
        : '';
      updatedObj.shipperEIC = shipperEIC;
      updatedObj.counterPartyId = selectedMarketPartyId;
      // clear the date selection when onBehalfOfMarketPartyId changes
      // As every marketparty has their own valid start/end of relationship authorization
      updatedObj.date = null;
      updatedObj.amounts = this.getClearedAmounts();
    }

    // If the previously selected gas day had more hours than the new gas day
    // due to a DST transition, the amounts corresponding to the extra hours
    // are discarded.
    if (field === 'date') {
      const length = hoursInGasDay(value);
      updatedObj.amounts = this.getClearedAmounts(length);
    }

    // Update BC summary for nomination dialog
    if (updatedObj.point === LOCATION_POINTS.BALTIC_CONNECTOR) {
      const {
        updateCapacityBCSummary,
      } = this.context;

      if (updatedObj.date && updateCapacityBCSummary) {
        // values available for few next gasdays
        const date = startOfOngoingGasDay();
        const start = date.format('YYYY-MM-DD');
        const end = date.add(7, 'days').format('YYYY-MM-DD');
        updateCapacityBCSummary(start, end);
      }
    }

    this.updateChangedFields(field, updatedObj, error);
  }

  getWarnings = () => {
    const { nomination } = this.state;
    const { nomints } = this.context;
    return getNominationWarnings(nomination, nomints);
  }

  renderActionButtons = ({ warnings }) => {
    const { t, canViewOnly } = this.props;
    const { isSaving } = this.state;
    const saveLabelKey = this.isRenomination() ? 'sendRenomination' : 'sendNomination';
    const cancelLabelKey = canViewOnly ? 'close' : 'cancel';
    const warningCount = warnings.length;

    const buttons = [
      <Button
        className="new-nomination__cancel-button"
        key="cancel"
        onClick={this.handleCancel}
      >
        {t(`common.button.${cancelLabelKey}`)}
      </Button>,
    ];
    if (!canViewOnly) {
      buttons.push((
        <Button
          className="new-nomination__create-button"
          key="save"
          loading={isSaving}
          type="primary"
          onClick={this.handleCreate}
          disabled={this.isButtonDisabled()}
        >
          {t(`nominations.button.${saveLabelKey}`)}
        </Button>
      ));
    }
    buttons.push(
      <span
        key="warnings"
        className="new-nomination__warning-count"
        style={{ visibility: warningCount > 0 ? 'visible' : 'hidden' }}
      >
        {t('nominations.warning.count', { count: warningCount })}
      </span>,
    );
    return buttons;
  }

  isValidRelationshipDate = (date) => {
    const {
      nomination: {
        onBehalfOfMarketPartyId,
      },
    } = this.state;
    const { relationships } = this.context;
    const isValid = (relationship) => date.isSameOrBefore(
      moment(relationship.end, 'YYYY-MM-DD'), 'day',
    ) && date.isSameOrAfter(
      moment(relationship.start, 'YYYY-MM-DD'), 'day',
    );
    // Enable all dates for which the relationship is valid.
    return relationships.some(r => r.marketPartyIdA === onBehalfOfMarketPartyId && isValid(r));
  }

  checkIfNominationDateIsAllowed = (date) => {
    const {
      nomination: {
        onBehalfOfMarketPartyId,
      },
    } = this.state;

    if (!isDateAllowed(date)) return false;

    // if nomination is done onBehalfOfMarketPartyId, date is allowed without balancegroup check
    if (onBehalfOfMarketPartyId) {
      return this.isValidRelationshipDate(date);
    }
    return this.isBalanceGroupMemberOnDate(startOfGasDay(date));
  }

  render = () => {
    const {
      t,
      nominationData = {},
      canViewOnly,
    } = this.props;
    const { nomresAmounts } = nominationData;
    const {
      errors,
      nomination,
    } = this.state;
    const {
      marketPartyIndex,
      relationships,
      selectedMarketPartyId,
      getSelectedMarketParty,
    } = this.context;

    let titleKey = this.isRenomination() ? 'renomination' : 'newNomination';
    if (canViewOnly) {
      titleKey = 'nomination';
    }

    const nominationWarnings = this.getWarnings();
    return (
      <ModalWrapper
        modalClassName="new-nomination"
        title={t(`nominations.title.${titleKey}`)}
        handleClose={this.handleCancel}
        actionButtons={this.renderActionButtons(nominationWarnings)}
        width="72rem"
      >
        <NominationForm
          amountsColumnCount={3}
          errors={errors}
          isDateAllowed={this.checkIfNominationDateIsAllowed}
          onChange={this.handleChange}
          nomination={nomination}
          nomresAmounts={nomresAmounts}
          marketPartyIndex={marketPartyIndex}
          isShipper={userHasPermission(this.context, [PERMISSIONS.SHIPPER])}
          relationships={relationships}
          selectedMarketPartyId={selectedMarketPartyId}
          getSelectedMarketParty={getSelectedMarketParty}
          isRenomination={this.isRenomination()}
          viewOnly={canViewOnly}
          nominationWarnings={nominationWarnings}
        />
      </ModalWrapper>
    );
  }
}

NewNomination.propTypes = {
  t: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  nominationData: PropTypes.shape({
    amounts: PropTypes.arrayOf(PropTypes.number),
    nomresAmounts: PropTypes.shape({}),
    date: PropTypes.string,
    direction: PropTypes.string,
    point: PropTypes.string,
    shipperEIC: PropTypes.string,
  }),
  canViewOnly: PropTypes.bool,
};

NewNomination.displayName = 'NewNomination';
NewNomination.contextType = Context;

const TRANSLATED = withTranslation()(NewNomination);

export default withRouter(TRANSLATED);
export {
  NewNomination as PureComponent,
};
